import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../UserContext';
import 'leaflet/dist/leaflet.css';
import MapDisplay from '../components/MapDisplay';
import MapDataInput from '../components/MapDataInput';
import MapVisualizationSettings from '../components/MapVisualizationSettings';
import MapLoadingState from '../components/MapLoadingState';
import MapDataUpload from '../components/MapDataUpload';
import TestResultUpload from '../components/TestResultUpload';
import WellTestingInfoModal from '../components/WellTestingInfoModal';
import { WellTestingTabs } from '../components/WellTestingTabs';
import { useWellTestingData } from '../hooks/useWellTestingData';
import { useVisualizationSettings } from '../hooks/useVisualizationSettings';
import { createColorIcon, availableColors } from '../utils/markerUtils';
import { getUniqueValues } from '../utils/dataParsingUtils';

function WellTestingPage() {
    const { user } = useUser();
    const [showInfoModal, setShowInfoModal] = useState(true);
    const [activeTab, setActiveTab] = useState('map');

    const {
        tableData,
        headers,
        locations,
        addressField,
        isLoading,
        handleTablePaste,
        handleFileUpload,
        handleAddressFieldSelect
    } = useWellTestingData();

    const {
        visualSettings,
        handleVisualizationChange,
        handleColorRuleChange
    } = useVisualizationSettings();

    return (
        <>
            <Helmet>
                <title>Well Testing</title>
                <meta name="description" content="Visualizations and data for Asheville well testing results." />
            </Helmet>
            
            <WellTestingInfoModal 
                isOpen={showInfoModal} 
                onClose={() => setShowInfoModal(false)} 
                setActiveTab={setActiveTab}
            />

            <div className="p-4">
                <WellTestingTabs activeTab={activeTab} setActiveTab={setActiveTab} />

                {activeTab === 'map' ? (
                    <div className="h-[calc(100vh-150px)] flex">
                        {/* Left Sidebar - 30% */}
                        <div className="w-[30%] p-4 border-r overflow-y-auto bg-gray-50">
                            <h1 className="text-2xl font-bold mb-4">Well Testing Data</h1>
                            
                            <div className="space-y-6">
                                <MapDataInput 
                                    headers={headers}
                                    addressField={addressField}
                                    onTablePaste={handleTablePaste}
                                    onAddressFieldSelect={handleAddressFieldSelect}
                                    onFileUpload={handleFileUpload}
                                />

                                {locations.length > 0 && (
                                    <MapVisualizationSettings 
                                        headers={headers}
                                        visualSettings={visualSettings}
                                        onVisualizationChange={handleVisualizationChange}
                                        onColorRuleChange={handleColorRuleChange}
                                        getUniqueValues={(field) => getUniqueValues(field, headers, locations)}
                                        availableColors={availableColors}
                                    />
                                )}

                                <MapLoadingState isLoading={isLoading} />

                                {locations.length > 0 && (
                                    <MapDataUpload 
                                        headers={headers}
                                        tableData={tableData}
                                        locations={locations}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Right Map Section - 70% */}
                        <div className="w-[70%] h-full">
                            <MapDisplay 
                                locations={locations}
                                headers={headers}
                                visualSettings={visualSettings}
                                createColorIcon={createColorIcon}
                                selectedBasemap={visualSettings.basemap}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="max-w-2xl mx-auto">
                        <TestResultUpload />
                    </div>
                )}
            </div>
        </>
    );
}

export default WellTestingPage;
